import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cart ID stored in localStorage
const CART_ID_KEY = 'shopify_cart_id';
const DISCOUNT_CODE_KEY = 'applied_discount_code';

export const cartService = {
  /**
   * Get cart ID from localStorage
   */
  getCartId() {
    return localStorage.getItem(CART_ID_KEY);
  },

  /**
   * Save cart ID to localStorage
   */
  setCartId(cartId) {
    localStorage.setItem(CART_ID_KEY, cartId);
  },

  /**
   * Clear cart ID from localStorage
   */
  clearCartId() {
    localStorage.removeItem(CART_ID_KEY);
  },

  /**
   * Discount code helpers — persisted so a code copied on the sale page
   * is auto-applied at Shopify checkout regardless of which page they're on
   */
  getDiscountCode() {
    return localStorage.getItem(DISCOUNT_CODE_KEY) || '';
  },
  setDiscountCode(code) {
    if (code) localStorage.setItem(DISCOUNT_CODE_KEY, code);
  },
  clearDiscountCode() {
    localStorage.removeItem(DISCOUNT_CODE_KEY);
  },

  /**
   * Create a new cart
   */
  async createCart(lines = []) {
    try {
      const response = await axios.post(`${API}/cart/create`, { lines });
      if (response.data.id) {
        this.setCartId(response.data.id);
      }
      return response.data;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  },

  /**
   * Add items to cart
   */
  async addToCart(variantId, quantity = 1) {
    try {
      const cartId = this.getCartId();
      const response = await axios.post(`${API}/cart/add`, {
        cartId,
        lines: [{ variantId, quantity }]
      });
      
      if (response.data.id) {
        this.setCartId(response.data.id);
      }
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  },

  /**
   * Get current cart
   */
  async getCart() {
    try {
      const cartId = this.getCartId();
      if (!cartId) {
        return null;
      }
      // URL encode the cart ID for the path parameter
      const encodedCartId = encodeURIComponent(cartId);
      const response = await axios.get(`${API}/cart/${encodedCartId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        this.clearCartId();
        return null;
      }
      console.error('Error getting cart:', error);
      throw error;
    }
  },

  /**
   * Update cart line quantity
   */
  async updateCartLine(lineId, quantity) {
    try {
      const cartId = this.getCartId();
      if (!cartId) {
        throw new Error('No cart found');
      }
      // URL encode the cart ID for the path parameter
      const encodedCartId = encodeURIComponent(cartId);
      const response = await axios.put(`${API}/cart/${encodedCartId}/update`, {
        lineId,
        quantity
      });
      return response.data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  },

  /**
   * Remove items from cart
   */
  async removeFromCart(lineIds) {
    try {
      const cartId = this.getCartId();
      if (!cartId) {
        throw new Error('No cart found');
      }
      // URL encode the cart ID for the path parameter
      const encodedCartId = encodeURIComponent(cartId);
      // Ensure lineIds is always an array
      const ids = Array.isArray(lineIds) ? lineIds : [lineIds];
      const response = await axios.delete(`${API}/cart/${encodedCartId}/remove`, {
        data: { lineIds: ids }
      });
      return response.data;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  },

  /**
   * Get checkout URL and redirect (with optional discount code appended)
   */
  async checkout() {
    const cartId = this.getCartId();
    if (!cartId) {
      throw new Error('No cart found');
    }
    const cart = await this.getCart();
    if (cart?.checkoutUrl) {
      let url = cart.checkoutUrl;
      const code = this.getDiscountCode();
      if (code) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}discount=${encodeURIComponent(code)}`;
      }
      window.location.href = url;
    }
  }
};

export default cartService;
